import LegalLayout from '@/components/layout/LegalLayout';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

export default function PrivacyPage() {
  useSEO({
    title: 'Privacy Policy | Acodia',
    description: 'How Acodia Ltd collects, uses, and protects your personal data under UK GDPR. We never sell or share your data for advertising.',
    path: '/privacy',
    ogImage: `${SITE_URL}/images/og/og-default.png`,
  });

  return (
    <LegalLayout eyebrow="Legal" title="Privacy Policy" lastUpdated="26 May 2026" testId="privacy-page">
      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy explains how <strong>Acodia Ltd</strong> ("Acodia", "we", "us") collects, uses,
        and protects your personal data when you use Acodia Watch and our website. Acodia Ltd is a company
        registered in England and Wales, Companies House number <strong>15412512</strong>, with its
        registered office at <strong>20 Wenlock Road, London, N1 7GU, United Kingdom</strong>.
      </p>
      <p>
        We are the data controller for the personal data described here, and we are registered with the UK
        Information Commissioner's Office (ICO) under registration number <strong>ZB786356</strong>. This
        policy covers our website (currently <a href="https://acodia-platform.vercel.app">acodia-platform.vercel.app</a>,
        moving to acodia.co.uk) and the Acodia Watch service.
      </p>
      <p><strong>Effective date:</strong> 26 May 2026. <strong>Last updated:</strong> 26 May 2026.</p>

      <h2>2. Data We Collect</h2>
      <ul>
        <li><strong>Account data</strong> — your email address, your name (if you provide it), and a securely hashed password. We never store your password in plain text.</li>
        <li><strong>Payment data</strong> — handled entirely by Stripe. We never see or store your full card number. We may receive limited details such as the last four digits and card expiry for display in your billing area.</li>
        <li><strong>Domain monitoring data</strong> — the domain names you choose to verify and monitor, and the technical scan results we generate for them.</li>
        <li><strong>Usage data</strong> — login events, scan triggers, and dashboard activity, used to operate and secure the service.</li>
        <li><strong>Technical data</strong> — your IP address, browser user agent, and session tokens, used for session security and fraud prevention.</li>
      </ul>
      <p>
        We do <strong>not</strong> scrape personal data from the domains you monitor — we analyse only their
        publicly visible security configuration (SSL/TLS, HTTP headers, DNS records, software fingerprints,
        certificate expiry, and public blacklist status).
      </p>

      <h2>3. How We Use Your Data</h2>
      <ul>
        <li><strong>To provide the service</strong> — run scans, deliver alerts, and generate recommendations, reports, and scan history.</li>
        <li><strong>To communicate with you</strong> — transactional emails for domain verification, security alerts, and billing. These are part of the service, not marketing.</li>
        <li><strong>To improve the service</strong> — aggregated, non-identifying analysis of how features are used. This is never sold or shared for advertising.</li>
        <li><strong>To comply with the law</strong> — where we are required to retain records or respond to lawful requests.</li>
      </ul>

      <h2>4. Lawful Basis for Processing (UK GDPR Article 6)</h2>
      <ul>
        <li><strong>Contract</strong> — processing necessary to provide the Acodia Watch service you have subscribed to.</li>
        <li><strong>Legitimate interests</strong> — security, fraud prevention, and improving our service, balanced against your rights.</li>
        <li><strong>Consent</strong> — where we ever send optional marketing communications, we rely on your separate opt-in, which you can withdraw at any time.</li>
        <li><strong>Legal obligation</strong> — retaining tax and accounting records and meeting regulatory requirements.</li>
      </ul>

      <h2>5. Subprocessors</h2>
      <p>
        We use carefully selected third-party processors to operate the service. Each is bound by data
        processing terms. Where data is transferred outside the UK, those transfers are covered by Standard
        Contractual Clauses and equivalent safeguards.
      </p>
      <div className="legal-table-wrap">
        <table data-testid="subprocessors-table">
          <thead>
            <tr><th>Processor</th><th>Purpose</th><th>Location</th></tr>
          </thead>
          <tbody>
            <tr><td>Stripe</td><td>Payment processing</td><td>Ireland (EU)</td></tr>
            <tr><td>Railway</td><td>Backend application hosting</td><td>United States</td></tr>
            <tr><td>Vercel</td><td>Frontend application hosting</td><td>United States</td></tr>
            <tr><td>Supabase</td><td>Database (primary data store)</td><td>Ireland (EU)</td></tr>
            <tr><td>Resend</td><td>Transactional email delivery</td><td>United States / Ireland</td></tr>
            <tr><td>Microsoft 365</td><td>Business email (connect@acodia.co.uk)</td><td>EU</td></tr>
            <tr><td>Anthropic</td><td>AI-generated security briefings — technical domain scan results only, no personal data</td><td>United States</td></tr>
            <tr><td>NVD, VirusTotal, Google Safe Browsing</td><td>Public security databases — we send domain names only, no personal data</td><td>United States</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>We do not sell or share your personal data for advertising purposes.</strong> We do not track
        you across the web, and we do not build advertising profiles.
      </p>

      <h2>6. Data Retention</h2>
      <p>We keep personal data only as long as necessary for the purposes above. In general:</p>
      <ul>
        <li><strong>Account data</strong> — retained while your account is active, and for up to 30 days after deletion.</li>
        <li><strong>Scan history</strong> — we aim to retain scan history broadly in line with your plan (Essential approximately 7 days, Professional approximately 90 days, Business for the life of your subscription). We may retain data for longer where necessary for security, legal, or operational reasons, subject to our internal retention practices.</li>
        <li><strong>Backups</strong> — retained for up to 30 days.</li>
        <li><strong>Logs</strong> — retained for up to 90 days.</li>
      </ul>

      <h2>7. Your Rights Under UK GDPR</h2>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
        <li><strong>Rectification</strong> — ask us to correct inaccurate or incomplete data.</li>
        <li><strong>Erasure</strong> — ask us to delete your personal data ("right to be forgotten").</li>
        <li><strong>Data portability</strong> — receive your data in a structured, machine-readable format.</li>
        <li><strong>Restriction</strong> — ask us to limit how we process your data.</li>
        <li><strong>Objection</strong> — object to processing based on legitimate interests.</li>
        <li><strong>Withdraw consent</strong> — withdraw any consent you have given, at any time.</li>
        <li><strong>Complain to the ICO</strong> — lodge a complaint at <a href="https://ico.org.uk/concerns">ico.org.uk/concerns</a>.</li>
      </ul>
      <p>
        To exercise any of these rights, email <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.
        We will respond within one month, as required by UK GDPR.
      </p>

      <h2>8. International Transfers</h2>
      <p>
        Some of our subprocessors are based in the United States (Railway, Vercel, Anthropic, and parts of
        Resend). Where personal data is transferred outside the UK, we rely on Standard Contractual Clauses
        and equivalent safeguards. Our primary database is hosted in the EU (Supabase, Ireland).
      </p>

      <h2>9. Security</h2>
      <ul>
        <li>Encryption in transit (TLS) and at rest.</li>
        <li>Access controls limiting who can access personal data.</li>
        <li>Regular security reviews of our own systems.</li>
        <li>Breach notification: where a personal data breach is likely to result in a risk to your rights, we will notify affected customers and the ICO within 72 hours of becoming aware, as required by UK GDPR.</li>
      </ul>

      <h2>10. Children</h2>
      <p>
        Acodia Watch is a business service not intended for individuals under 18. We do not knowingly collect
        personal data from children. If you believe a child has provided us data, contact us and we will
        delete it.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this policy from time to time. We will notify customers of material changes and update
        the "Last updated" date at the top of this page.
      </p>

      <h2>12. Contact</h2>
      <p>
        Data protection contact: <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a><br />
        Postal: Acodia Ltd, 20 Wenlock Road, London, N1 7GU, United Kingdom<br />
        Companies House: 15412512<br />
        ICO Registration: ZB786356<br />
        Complaints: the Information Commissioner's Office at <a href="https://ico.org.uk">ico.org.uk</a>
      </p>
      <hr />
      <p>Have questions? Email <a href="mailto:connect@acodia.co.uk">connect@acodia.co.uk</a>.</p>
    </LegalLayout>
  );
}
